import { useCallback, useMemo, useState } from 'react';
import { POLICY_CATEGORY_ESCALATION } from './policy-constants';
import { PolicyCategoryGroup } from './policy-groups';

export interface PolicyCategoryOpenState {
  isOpen: (category: string) => boolean;
  toggle: (category: string) => void;
  /** Force open — deep links and picking a category in the filters. Never folds. */
  open: (category: string) => void;
}

interface PolicyCategoryOpenOptions {
  /** Start every category open, for surfaces where users want every policy in view. */
  openAllByDefault?: boolean;
}

/**
 * Escalation always starts open: it is the group the front line reaches for
 * mid-call, so it must not hide behind a click. Every other category starts open
 * only when it holds a restriction, unless the surface opts into opening them all.
 * A user's click is recorded as an override layered on that default, so a
 * refetch can neither re-fold what they opened nor re-open what they folded.
 */
const isOpenByDefault = (group: PolicyCategoryGroup): boolean =>
  group.category === POLICY_CATEGORY_ESCALATION || group.restrictionCount > 0;

export const usePolicyCategoryOpen = (
  groups: PolicyCategoryGroup[],
  { openAllByDefault = false }: PolicyCategoryOpenOptions = {},
): PolicyCategoryOpenState => {
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});

  const defaultOpen = useMemo(
    () =>
      Object.fromEntries(
        groups.map((group) => [group.category, openAllByDefault || isOpenByDefault(group)]),
      ),
    [groups, openAllByDefault],
  );

  const isOpen = useCallback(
    (category: string) => overrides[category] ?? defaultOpen[category] ?? false,
    [overrides, defaultOpen],
  );

  const toggle = useCallback(
    (category: string) =>
      setOverrides((prev) => ({
        ...prev,
        [category]: !(prev[category] ?? defaultOpen[category] ?? false),
      })),
    [defaultOpen],
  );

  const open = useCallback(
    (category: string) => setOverrides((prev) => ({ ...prev, [category]: true })),
    [],
  );

  return useMemo(() => ({ isOpen, toggle, open }), [isOpen, toggle, open]);
};
